// Active abilities (plan extension): one unlocks every 5 levels, cast with a hotkey.
// The system tracks per-ability cooldowns + active-buff timers, exposes the derived
// player buffs (damage/speed/invulnerability) the World feeds into combat, and applies
// the instant / ongoing area effects. Effects operate on the World (passed in), reusing
// the enemy hash, dealDamage, beams and particle bursts.

import { ABILITIES, ABILITY, MAX_ENEMY_RADIUS } from './balance';
import type { AbilityDef, AbilityEffectKey } from './balance';
import { dealDamage } from './combat';
import { spawnBeam } from './fx';
import type { World } from './world';
import type { Enemy } from './enemies';

export type ArenaAbilityFieldKind =
  | 'rootSnare'
  | 'nectarGrove'
  | 'timeLock'
  | 'eventHorizon'
  | 'railSurge'
  | 'bookHole';

export interface ArenaAbilityField {
  kind: ArenaAbilityFieldKind;
  x: number;
  y: number;
  radius: number;
  lifeSec: number;
  maxLifeSec: number;
  tickSec: number;
  x2?: number;
  y2?: number;
}

export type ArenaAbilityAgentKind = 'harvestSwarm' | 'conduitPylon' | 'windStar';

export interface ArenaAbilityAgent {
  kind: ArenaAbilityAgentKind;
  x: number;
  y: number;
  radius: number;
  lifeSec: number;
  maxLifeSec: number;
  tickSec: number;
}

export type ArenaAbilityVisualKind =
  | 'symbolWave'
  | 'flameWave'
  | 'localizedBurst'
  | 'mycelialTendrils'
  | 'meteor';

export interface ArenaAbilityVisual {
  kind: ArenaAbilityVisualKind;
  texture: string;
  x: number;
  y: number;
  radius: number;
  color: number;
  alternateColor?: number;
  count: number;
  size: number;
  rotation: number;
  lifeSec: number;
  maxLifeSec: number;
}

export class AbilitySystem {
  /** Cooldown remaining per ability (seconds); 0 = ready. */
  readonly cd: number[];
  /** Active-buff time remaining per ability (seconds). */
  readonly active: number[];
  private readonly tickAcc: number[]; // periodic-effect accumulators
  private readonly buf: number[] = []; // scratch for area queries
  private mycelialPulseIndex = 0;
  private solarPulseIndex = 0;
  private nextAbilityProjectileId = -1;
  // NinjaKid jutsu state (single-instance: every cooldown far exceeds its duration).
  private windStarPhase = 0;
  private spiritSweepIndex = 0;
  private discReturnFired = false;

  /** Persistent, renderer-readable effects used only by the two expansion kits. */
  readonly arenaFields: ArenaAbilityField[] = [];
  readonly arenaAgents: ArenaAbilityAgent[] = [];
  readonly arenaVisuals: ArenaAbilityVisual[] = [];

  // Derived per-tick player buffs (read by the World).
  dmgMul = 1;
  dmgMulClose = 1;
  closeRadius2 = 0;
  speedMul = 1;
  invuln = false;
  damageTakenMul = 1;
  glowColor = -1; // color of the active buff aura, or -1

  constructor(private readonly defs: ReadonlyArray<AbilityDef> = ABILITIES) {
    const n = defs.length;
    this.cd = new Array(n).fill(0);
    this.active = new Array(n).fill(0);
    this.tickAcc = new Array(n).fill(0);
  }

  isUnlocked(i: number, level: number): boolean {
    return level >= this.defs[i].unlockLevel;
  }

  /** Activate the ability bound to `code` if unlocked + off cooldown. */
  activateByHotkey(code: string, level: number, world: World): boolean {
    for (let i = 0; i < this.defs.length; i++) {
      if (this.defs[i].hotkey === code) return this.activate(i, level, world);
    }
    return false;
  }

  activate(i: number, level: number, world: World): boolean {
    const def = this.defs[i];
    if (def === undefined || (level < def.unlockLevel && !world.allAbilities) || this.cd[i] > 0) return false;
    this.cd[i] = def.cooldownSec;
    this.active[i] = def.durationSec; // 0 for instant
    this.tickAcc[i] = 0;
    this.castFx(i, world);
    this.fire(i, world);
    world.emitHaptic('ability');
    return true;
  }

  /** Tick cooldowns + active timers, run ongoing/expiry effects, recompute buffs. */
  update(dt: number, world: World): void {
    for (let i = 0; i < this.defs.length; i++) {
      if (this.cd[i] > 0) this.cd[i] -= dt;
      if (this.active[i] > 0) {
        this.active[i] -= dt;
        this.tickActive(i, dt, world);
        if (this.active[i] <= 0) this.onExpire(i, world);
      }
    }
    this.updateArenaVisuals(dt, world);
    this.updateArenaEffects(dt, world);
    this.updateBlight(dt, world);
    this.recomputeBuffs();
  }

  private recomputeBuffs(): void {
    this.dmgMul = 1;
    this.dmgMulClose = 1;
    this.closeRadius2 = 0;
    this.speedMul = 1;
    this.invuln = false;
    this.damageTakenMul = 1;
    this.glowColor = -1;
    for (let i = 0; i < this.defs.length; i++) {
      if (this.active[i] <= 0) continue;
      const key = this.defs[i].key;
      if (key === 'thornskin') {
        this.damageTakenMul = 0.5;
        this.glowColor = this.defs[i].color;
        continue;
      }
      // NinjaKid jutsus: bespoke buffs; never fall through to the Base Lab effects.
      if (key === 'stoneJutsu') {
        this.damageTakenMul = 0.35;
        this.glowColor = this.defs[i].color;
        continue;
      }
      if (key === 'sexyJutsu') {
        this.invuln = true;
        if (this.glowColor < 0) this.glowColor = this.defs[i].color;
        continue;
      }
      if (key === 'blueLightning') {
        this.speedMul = 1.8;
        if (this.glowColor < 0) this.glowColor = this.defs[i].color;
        continue;
      }
      if (
        key === 'mycelialGrasp'
        || key === 'fireflyRush'
        || key === 'harvestSwarm'
        || key === 'meteorMantle'
        || key === 'orbitGuard'
        || key === 'solarEruption'
        || key === 'conduitStorm'
        || key === 'windStar'
        || key === 'ninjaDisc'
        || key === 'outgame'
        || key === 'fireJutsu'
        || key === 'spiritSmash'
      ) {
        if (this.glowColor < 0) this.glowColor = this.defs[i].color;
        continue;
      }
      switch (this.effectAt(i)) {
        case 'berserk':
          this.dmgMul = ABILITY.berserkDmgMul;
          this.dmgMulClose = ABILITY.berserkCloseMul;
          this.closeRadius2 = ABILITY.berserkCloseRadius * ABILITY.berserkCloseRadius;
          this.invuln = true;
          this.glowColor = this.defs[i].color;
          break;
        case 'aegis':
          this.invuln = true;
          if (this.glowColor < 0) this.glowColor = this.defs[i].color;
          break;
        case 'tempest':
          this.speedMul = ABILITY.tempestSpeedMul;
          if (this.glowColor < 0) this.glowColor = this.defs[i].color;
          break;
      }
    }
  }

  // Instant effects fired on activation.
  private fire(i: number, world: World): void {
    if (this.fireArenaAbility(i, world)) return;
    switch (this.effectAt(i)) {
      case 'cryo':
        this.freezeArea(world, ABILITY.cryoRadius, ABILITY.cryoSlowSec, ABILITY.cryoSlowMul);
        break;
      case 'transmute':
        this.damageArea(world, ABILITY.transmuteRadius, ABILITY.transmuteDamage);
        break;
      case 'aegis':
        world.player.hp = Math.min(world.player.maxHp, world.player.hp + ABILITY.aegisHeal);
        this.knockback(world, ABILITY.aegisKnockRadius, ABILITY.aegisKnockback);
        break;
      case 'meltdown':
        this.igniteArea(world, ABILITY.meltdownRadius, ABILITY.meltdownBurnSec, ABILITY.meltdownBurnDps);
        break;
      // berserk / blackhole / tempest / overload act over their duration (tickActive).
    }
  }

  // Ongoing effects each tick while active.
  private tickActive(i: number, dt: number, world: World): void {
    if (this.tickArenaAbility(i, dt, world)) return;
    switch (this.effectAt(i)) {
      case 'blackhole':
        this.pull(world, ABILITY.blackholeRadius, ABILITY.blackholePullPerSec * dt);
        break;
      case 'tempest':
        this.tickAcc[i] += dt;
        while (this.tickAcc[i] >= ABILITY.tempestTickSec) {
          this.tickAcc[i] -= ABILITY.tempestTickSec;
          this.damageArea(world, ABILITY.tempestRadius, ABILITY.tempestDamage);
        }
        break;
      case 'overload':
        this.tickAcc[i] += dt;
        while (this.tickAcc[i] >= ABILITY.overloadTickSec) {
          this.tickAcc[i] -= ABILITY.overloadTickSec;
          this.overloadZap(world, this.defs[i].color);
        }
        break;
    }
  }

  // End-of-duration effects.
  private onExpire(i: number, world: World): void {
    const key = this.defs[i].key;
    if (key === 'blackhole') {
      const p = world.player;
      world.fxRing(p.x, p.y, ABILITY.blackholeBoomRadius, this.defs[i].color);
      world.fxNova(p.x, p.y, this.defs[i].color, 30, 200, 380);
      this.damageArea(world, ABILITY.blackholeBoomRadius, ABILITY.blackholeDamage);
    } else if (key === 'spiritSmash') {
      // The finale: a distortion smash that sends the whole crowd flying.
      const p = world.player;
      world.fxRing(p.x, p.y, 300, this.defs[i].color);
      world.fxNova(p.x, p.y, this.defs[i].color, 34, 200, 380);
      this.damageArea(world, 300, 160);
      this.knockback(world, 300, 220);
      world.shakeTrauma = Math.min(1, world.shakeTrauma + 0.45);
    }
  }

  // Cast blast: an expanding shockwave ring + radial particle nova from the player,
  // colored per ability (Overload is omitted - it has its own lightning storm).
  private castFx(i: number, world: World): void {
    if (this.castArenaFx(i, world)) return;
    const p = world.player;
    const def = this.defs[i];
    switch (this.effectAt(i)) {
      case 'berserk':
        world.fxRing(p.x, p.y, 120, def.color);
        world.fxNova(p.x, p.y, def.color, 28, 160, 300);
        break;
      case 'cryo':
        world.fxRing(p.x, p.y, 300, def.color, 0.5);
        world.fxNova(p.x, p.y, def.color, 36, 160, 320);
        break;
      case 'transmute':
        world.fxRing(p.x, p.y, ABILITY.transmuteRadius, def.color);
        world.fxNova(p.x, p.y, def.color, 34, 180, 340);
        break;
      case 'aegis':
        world.fxRing(p.x, p.y, ABILITY.aegisKnockRadius, def.color);
        world.fxNova(p.x, p.y, def.color, 28, 160, 300);
        break;
      case 'blackhole':
        world.fxImplosion(p.x, p.y, def.color, 30, 210);
        break;
      case 'tempest':
        world.fxRing(p.x, p.y, 140, def.color);
        world.fxNova(p.x, p.y, def.color, 24, 140, 260);
        break;
      case 'meltdown':
        world.fxRing(p.x, p.y, 300, def.color, 0.5);
        world.fxNova(p.x, p.y, def.color, 34, 160, 320);
        break;
    }
  }

  private effectAt(i: number): AbilityEffectKey {
    const def = this.defs[i];
    return def.effect ?? (def.key as AbilityEffectKey);
  }

  private castArenaFx(i: number, world: World): boolean {
    const def = this.defs[i];
    const radius = this.activationRadius(this.effectAt(i));
    const symbol = (
      texture: string,
      size: number,
      count = 32,
      kind: ArenaAbilityVisualKind = 'symbolWave',
      alternateColor?: number,
    ): void => {
      this.arenaVisuals.push({
        kind,
        texture,
        x: world.player.x,
        y: world.player.y,
        radius,
        color: def.color,
        alternateColor,
        count,
        size,
        rotation: 0,
        lifeSec: kind === 'flameWave' ? 0.52 : 0.5,
        maxLifeSec: kind === 'flameWave' ? 0.52 : 0.5,
      });
    };

    switch (def.key) {
      case 'thornskin':
        symbol('statusThorn', 13);
        return true;
      case 'rootSnare':
      case 'mycelialGrasp':
        symbol('statusRoot', 14);
        return true;
      case 'sporeburst':
      case 'blightWave':
        symbol('statusSpore', 13);
        return true;
      case 'nectarWard':
        symbol('projectileSap', 14);
        return true;
      case 'fireflyRush':
      case 'harvestSwarm':
        symbol('statusFirefly', 14);
        return true;
      case 'meteorMantle':
      case 'solarEruption':
        symbol('statusFlame', 19, 48, 'flameWave', 0xffd34d);
        return true;
      case 'timeLock':
        symbol('statusClock', 14);
        return true;
      case 'prismBurst':
        symbol('prismBolt', 15);
        return true;
      case 'orbitGuard':
        symbol('statusSatellite', 15);
        return true;
      case 'eventHorizon':
        symbol('abilityVoid', 13);
        return true;
      case 'railSurge':
        symbol('statusRail', 16);
        return true;
      case 'conduitStorm':
        symbol('abilityBolt', 14);
        return true;
      // NinjaKid Dojo jutsu cast flashes.
      case 'stoneJutsu':
        symbol('boulder', 12);
        return true;
      case 'windStar':
      case 'ninjaDisc':
        symbol('windShuriken', 15);
        return true;
      case 'sexyJutsu':
        symbol('healthOrb', 13);
        return true;
      case 'outgame':
        symbol('abilityVoid', 13);
        return true;
      case 'blueLightning':
        symbol('abilityBolt', 14);
        return true;
      case 'fireJutsu':
        symbol('statusFlame', 19, 48, 'flameWave', 0xffd34d);
        return true;
      case 'spiritSmash':
        symbol('abilitySpark', 14);
        return true;
      default:
        return false;
    }
  }

  private activationRadius(effect: AbilityEffectKey): number {
    switch (effect) {
      case 'berserk':
        return 120;
      case 'cryo':
      case 'meltdown':
        return 300;
      case 'transmute':
        return 230;
      case 'aegis':
        return 175;
      case 'blackhole':
        return 210;
      case 'tempest':
      case 'overload':
        return 140;
    }
  }

  private fireArenaAbility(i: number, world: World): boolean {
    const key = this.defs[i].key;
    const p = world.player;
    switch (key) {
      case 'thornskin':
        return true;
      case 'rootSnare':
        this.arenaFields.push({
          kind: 'rootSnare', x: p.x, y: p.y, radius: 190,
          lifeSec: 6, maxLifeSec: 6, tickSec: 0,
        });
        return true;
      case 'sporeburst':
        this.forEachEnemyInRadius(world, p.x, p.y, 280, (enemy) => {
          enemy.sporeMarkedSec = Math.max(enemy.sporeMarkedSec, 9);
        });
        return true;
      case 'nectarWard':
        this.arenaFields.push({
          kind: 'nectarGrove', x: p.x, y: p.y, radius: 145,
          lifeSec: 10, maxLifeSec: 10, tickSec: 0,
        });
        return true;
      case 'mycelialGrasp':
        this.mycelialPulseIndex = 0;
        this.performMycelialPulse(world);
        return true;
      case 'fireflyRush':
        this.fireflyVolley(world);
        return true;
      case 'blightWave':
        this.forEachEnemyInRadius(world, p.x, p.y, 500, (enemy) => {
          enemy.burnSec = Math.max(enemy.burnSec, 9);
          enemy.burnDps = Math.max(enemy.burnDps, 34);
          enemy.burnTint = 0xd680d8;
          enemy.blightSpreadCd = world.randomRange(0.25, 0.7);
        });
        return true;
      case 'harvestSwarm':
        for (let k = 0; k < 3; k++) {
          const angle = (k / 3) * Math.PI * 2;
          this.arenaAgents.push({
            kind: 'harvestSwarm',
            x: p.x + Math.cos(angle) * 54,
            y: p.y + Math.sin(angle) * 54,
            radius: 68,
            lifeSec: 8,
            maxLifeSec: 8,
            tickSec: 0,
          });
        }
        return true;
      case 'meteorMantle':
        this.callMeteorStrike(world);
        return true;
      case 'timeLock':
        this.arenaFields.push({
          kind: 'timeLock', x: p.x, y: p.y, radius: 220,
          lifeSec: 6, maxLifeSec: 6, tickSec: 0,
        });
        return true;
      case 'prismBurst':
        this.firePrismBurst(world);
        return true;
      case 'orbitGuard':
        this.orbitGuardVolley(world);
        return true;
      case 'eventHorizon':
        this.arenaFields.push({
          kind: 'eventHorizon', x: p.x, y: p.y, radius: 230,
          lifeSec: 5, maxLifeSec: 5, tickSec: 0,
        });
        return true;
      case 'railSurge': {
        const rail = world.dashPlayer(300);
        this.arenaFields.push({
          kind: 'railSurge',
          x: rail.x1,
          y: rail.y1,
          x2: rail.x2,
          y2: rail.y2,
          radius: 30,
          lifeSec: 4,
          maxLifeSec: 4,
          tickSec: 0,
        });
        this.spawnLocalizedBurst(rail.x2, rail.y2, 'statusRail', 0x74edff, 70, 20, 12);
        return true;
      }
      case 'solarEruption':
        this.solarPulseIndex = 0;
        this.performSolarSweep(world);
        return true;
      case 'conduitStorm':
        for (let k = 0; k < 3; k++) {
          const angle = (k / 3) * Math.PI * 2;
          this.arenaAgents.push({
            kind: 'conduitPylon',
            x: p.x + Math.cos(angle) * 155,
            y: p.y + Math.sin(angle) * 155,
            radius: 28,
            lifeSec: 8,
            maxLifeSec: 8,
            tickSec: 0,
          });
        }
        return true;
      // --- NinjaKid Dojo: eight bespoke jutsus ---
      case 'stoneJutsu':
        // Pure stance: the buff lives in recomputeBuffs, the counter in onContactHit.
        return true;
      case 'windStar':
        this.windStarPhase = 0;
        this.arenaAgents.push({
          kind: 'windStar', x: p.x, y: p.y, radius: 56,
          lifeSec: 3.5, maxLifeSec: 3.5, tickSec: 0,
        });
        return true;
      case 'ninjaDisc':
        this.discReturnFired = false;
        this.fireDiscRing(world, false);
        return true;
      case 'sexyJutsu':
        p.hp = Math.min(p.maxHp, p.hp + 40);
        this.forEachEnemyInRadius(world, p.x, p.y, 340, (enemy) => {
          enemy.confusedSec = Math.max(enemy.confusedSec, 4);
          enemy.wanderAng = world.randomRange(0, Math.PI * 2);
        });
        return true;
      case 'outgame':
        this.arenaFields.push({
          kind: 'bookHole', x: p.x, y: p.y, radius: 260,
          lifeSec: 1.6, maxLifeSec: 1.6, tickSec: 0,
        });
        return true;
      case 'blueLightning': {
        const trail = world.dashPlayer(260);
        this.arenaFields.push({
          kind: 'railSurge',
          x: trail.x1,
          y: trail.y1,
          x2: trail.x2,
          y2: trail.y2,
          radius: 26,
          lifeSec: 2.5,
          maxLifeSec: 2.5,
          tickSec: 0,
        });
        this.blueLightningZap(world);
        return true;
      }
      case 'fireJutsu':
        this.fireConeWave(world);
        return true;
      case 'spiritSmash':
        this.spiritSweepIndex = 0;
        this.performSpiritSweep(world);
        return true;
      default:
        return false;
    }
  }

  private tickArenaAbility(i: number, dt: number, world: World): boolean {
    const key = this.defs[i].key;
    let interval = 0;
    let action: (() => void) | null = null;
    switch (key) {
      case 'thornskin':
      case 'harvestSwarm':
      case 'eventHorizon':
      case 'conduitStorm':
      case 'stoneJutsu':
      case 'windStar':
      case 'sexyJutsu':
      case 'outgame':
        return true;
      case 'ninjaDisc':
        // Half a beat after the throw, the discs come slicing back inward.
        this.tickAcc[i] += dt;
        if (!this.discReturnFired && this.tickAcc[i] >= 0.45) {
          this.discReturnFired = true;
          this.fireDiscRing(world, true);
        }
        return true;
      case 'blueLightning':
        interval = 0.28;
        action = () => this.blueLightningZap(world);
        break;
      case 'fireJutsu':
        interval = 0.42;
        action = () => this.fireConeWave(world);
        break;
      case 'spiritSmash':
        interval = 0.2;
        action = () => this.performSpiritSweep(world);
        break;
      case 'mycelialGrasp':
        interval = 0.55;
        action = () => this.performMycelialPulse(world);
        break;
      case 'fireflyRush':
        interval = 0.38;
        action = () => this.fireflyVolley(world);
        break;
      case 'meteorMantle':
        interval = 0.58;
        action = () => this.callMeteorStrike(world);
        break;
      case 'orbitGuard':
        this.interceptHostileShots(world);
        interval = 0.48;
        action = () => this.orbitGuardVolley(world);
        break;
      case 'solarEruption':
        interval = 0.2;
        action = () => this.performSolarSweep(world);
        break;
      default:
        return false;
    }
    this.tickAcc[i] += dt;
    while (this.tickAcc[i] >= interval) {
      this.tickAcc[i] -= interval;
      action();
    }
    return true;
  }

  private updateArenaEffects(dt: number, world: World): void {
    let nectarBoost = false;
    for (let i = this.arenaFields.length - 1; i >= 0; i--) {
      const field = this.arenaFields[i];
      field.lifeSec -= dt;
      field.tickSec -= dt;
      if (field.kind === 'rootSnare' && field.tickSec <= 0) {
        field.tickSec = 0.34;
        this.forEachEnemyInRadius(world, field.x, field.y, field.radius, (enemy) => {
          enemy.slowSec = Math.max(enemy.slowSec, 0.48);
          enemy.slowMul = Math.min(enemy.slowMul, 0.2);
          enemy.slowTint = 0xa6f2dc;
          dealDamage(world.hitCtx, enemy, 18, field.x, field.y, false);
        });
      } else if (field.kind === 'nectarGrove') {
        if (this.distanceSquared(field.x, field.y, world.player.x, world.player.y) <= field.radius * field.radius) {
          world.player.hp = Math.min(world.player.maxHp, world.player.hp + 8 * dt);
          nectarBoost = true;
        }
      } else if (field.kind === 'timeLock') {
        this.forEachEnemyInRadius(world, field.x, field.y, field.radius, (enemy) => {
          enemy.slowSec = Math.max(enemy.slowSec, 0.14);
          enemy.slowMul = 0;
          enemy.slowTint = 0xb2cdff;
        });
        for (let j = 0; j < world.enemyShots.count; j++) {
          const shot = world.enemyShots.items[j];
          if (this.distanceSquared(field.x, field.y, shot.x, shot.y) <= field.radius * field.radius) {
            shot.stasisSec = Math.max(shot.stasisSec, 0.14);
          }
        }
      } else if (field.kind === 'eventHorizon') {
        this.pullEnemiesToward(world, field.x, field.y, field.radius, 250 * dt);
        this.pullEnemyShotsToward(world, field.x, field.y, field.radius, 320 * dt);
        if (field.tickSec <= 0) {
          field.tickSec = 0.32;
          this.damageAreaAt(world, field.x, field.y, 82, 30);
        }
      } else if (field.kind === 'bookHole') {
        // Outgame: the book hole drags everything toward the open page.
        this.pullEnemiesToward(world, field.x, field.y, field.radius, 300 * dt);
        this.pullEnemyShotsToward(world, field.x, field.y, field.radius, 380 * dt);
        if (field.tickSec <= 0) {
          field.tickSec = 0.3;
          this.damageAreaAt(world, field.x, field.y, 110, 45);
        }
      } else if (field.kind === 'railSurge' && field.tickSec <= 0) {
        field.tickSec = 0.25;
        this.damageAlongRail(world, field);
      }

      if (field.lifeSec > 0) continue;
      if (field.kind === 'eventHorizon') {
        this.spawnLocalizedBurst(field.x, field.y, 'abilityVoid', 0x8bb5ff, 230, 20, 12);
        this.damageAreaAt(world, field.x, field.y, 230, 240);
      } else if (field.kind === 'bookHole') {
        // The book shuts: heavy damage all over, and fodder caught on the page is
        // simply taken out of the game (through the normal death flow, XP included).
        this.spawnLocalizedBurst(field.x, field.y, 'abilityVoid', 0x9b5cff, 260, 22, 12);
        this.damageAreaAt(world, field.x, field.y, field.radius, 300);
        this.forEachEnemyInRadius(world, field.x, field.y, 130, (enemy) => {
          if (!enemy.isBoss) dealDamage(world.hitCtx, enemy, 9999, field.x, field.y, false);
        });
        world.shakeTrauma = Math.min(1, world.shakeTrauma + 0.5);
      }
      this.arenaFields.splice(i, 1);
    }
    this.damageFieldMul = nectarBoost ? 1.4 : 1;

    let harvestActive = false;
    for (let i = this.arenaAgents.length - 1; i >= 0; i--) {
      const agent = this.arenaAgents[i];
      agent.lifeSec -= dt;
      agent.tickSec -= dt;
      if (agent.kind === 'harvestSwarm') {
        harvestActive = true;
        const target = this.nearestEnemy(world, agent.x, agent.y, 620);
        const tx = target?.x ?? world.player.x;
        const ty = target?.y ?? world.player.y;
        this.moveToward(agent, tx, ty, 290, dt);
        if (agent.tickSec <= 0) {
          agent.tickSec = 0.25;
          this.damageAreaAt(world, agent.x, agent.y, agent.radius, 44);
        }
      } else if (agent.kind === 'windStar') {
        // The giant shuriken spirals away from the ninja and swings back home.
        this.windStarPhase += dt;
        const progress = 1 - agent.lifeSec / agent.maxLifeSec;
        const distance = 70 + 330 * Math.sin(Math.PI * Math.min(1, Math.max(0, progress)));
        const angle = this.windStarPhase * 3.2;
        agent.x = world.player.x + Math.cos(angle) * distance;
        agent.y = world.player.y + Math.sin(angle) * distance;
        if (agent.tickSec <= 0) {
          agent.tickSec = 0.14;
          this.damageAreaAt(world, agent.x, agent.y, agent.radius, 55);
        }
      } else if (agent.tickSec <= 0) {
        agent.tickSec = 0.42;
        this.fireConduitPylon(world, agent);
      }
      if (agent.lifeSec <= 0) this.arenaAgents.splice(i, 1);
    }
    if (harvestActive) this.pullExperience(world, 620, 420, dt);
  }

  private updateBlight(dt: number, world: World): void {
    for (let i = 0; i < world.enemies.count; i++) {
      const source = world.enemies.items[i];
      if (source.sporeMarkedSec > 0) source.sporeMarkedSec = Math.max(0, source.sporeMarkedSec - dt);
      if (source.burnTint !== 0xd680d8 || source.burnSec <= 0) continue;
      source.blightSpreadCd -= dt;
      if (source.blightSpreadCd > 0) continue;
      source.blightSpreadCd = 0.65;
      const target = this.nearestEnemy(
        world,
        source.x,
        source.y,
        145,
        (enemy) => enemy !== source && !(enemy.burnTint === 0xd680d8 && enemy.burnSec > 0.5),
      );
      if (target === null) continue;
      target.burnSec = Math.max(target.burnSec, 4.5);
      target.burnDps = Math.max(target.burnDps, source.burnDps * 0.72);
      target.burnTint = 0xd680d8;
      target.blightSpreadCd = 1;
      spawnBeam(world.beams, source.x, source.y, target.x, target.y, 0xd680d8, 'spore');
    }
  }

  damageFieldMul = 1;

  onEnemyKilled(enemy: Enemy, world: World): void {
    if (enemy.sporeMarkedSec <= 0) return;
    this.spawnLocalizedBurst(
      enemy.x,
      enemy.y,
      'statusSpore',
      0xf35fca,
      118,
      18,
      11,
      0xb9e84f,
    );
    this.damageAreaAt(world, enemy.x, enemy.y, 118, 92);
  }

  onContactHit(enemy: Enemy, world: World): void {
    // Thornskin (Verdant) and Stone Jutsu (NinjaKid) both punish contact while active.
    const index = this.defs.findIndex(
      (definition) => definition.key === 'thornskin' || definition.key === 'stoneJutsu',
    );
    if (index < 0 || this.active[index] <= 0) return;
    const stone = this.defs[index].key === 'stoneJutsu';
    dealDamage(world.hitCtx, enemy, stone ? 90 : 70, enemy.x, enemy.y, false);
    const dx = enemy.x - world.player.x;
    const dy = enemy.y - world.player.y;
    const distance = Math.hypot(dx, dy) || 1;
    enemy.x += (dx / distance) * (stone ? 140 : 130);
    enemy.y += (dy / distance) * (stone ? 140 : 130);
    world.fxBurst(enemy.x, enemy.y, stone ? 0x9aa0a8 : 0x7fd34e, 8);
  }

  private performMycelialPulse(world: World): void {
    const p = world.player;
    const spokeCount = 6;
    const radius = 340;
    const rotation = this.mycelialPulseIndex++ * 0.17;
    for (let i = 0; i < world.enemies.count; i++) {
      const enemy = world.enemies.items[i];
      if (enemy.hp <= 0) continue;
      const dx = enemy.x - p.x;
      const dy = enemy.y - p.y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      if (distance > radius) continue;
      const step = (Math.PI * 2) / spokeCount;
      let offset = (Math.atan2(dy, dx) - rotation) % step;
      if (offset > step / 2) offset -= step;
      if (offset < -step / 2) offset += step;
      const tolerance = 0.12 + Math.min(0.18, enemy.radius / distance);
      if (Math.abs(offset) > tolerance) continue;
      enemy.slowSec = Math.max(enemy.slowSec, 2.2);
      enemy.slowMul = Math.min(enemy.slowMul, 0.28);
      enemy.slowTint = 0xa6f2dc;
      dealDamage(world.hitCtx, enemy, 68, enemy.x, enemy.y, false);
    }
    this.arenaVisuals.push({
      kind: 'mycelialTendrils',
      texture: 'statusRoot',
      x: p.x,
      y: p.y,
      radius,
      color: 0x8ecb63,
      alternateColor: 0xb9e84f,
      count: spokeCount,
      size: 14,
      rotation,
      lifeSec: 0.46,
      maxLifeSec: 0.46,
    });
  }

  private fireflyVolley(world: World): void {
    let x = world.player.x;
    let y = world.player.y;
    const visited = new Set<Enemy>();
    for (let i = 0; i < 6; i++) {
      const enemy = this.nearestEnemy(world, x, y, 560, (candidate) => !visited.has(candidate));
      if (enemy === null) break;
      visited.add(enemy);
      spawnBeam(world.beams, x, y, enemy.x, enemy.y, 0xffdf78, 'firefly');
      dealDamage(world.hitCtx, enemy, 48, enemy.x, enemy.y, false);
      x = enemy.x;
      y = enemy.y;
    }
  }

  private callMeteorStrike(world: World): void {
    const target = this.nearestEnemy(world, world.player.x, world.player.y, 680);
    if (target === null) return;
    this.arenaVisuals.push({
      kind: 'meteor',
      texture: 'projectileComet',
      x: target.x,
      y: target.y,
      radius: 112,
      color: 0xff8a45,
      count: 1,
      size: 34,
      rotation: -0.52,
      lifeSec: 0.3,
      maxLifeSec: 0.3,
    });
  }

  private impactMeteor(world: World, x: number, y: number): void {
    this.spawnLocalizedBurst(x, y, 'statusFlame', 0xff8a45, 112, 20, 12, 0xffd34d);
    this.forEachEnemyInRadius(world, x, y, 112, (enemy) => {
      enemy.burnSec = Math.max(enemy.burnSec, 3);
      enemy.burnDps = Math.max(enemy.burnDps, 22);
      enemy.burnTint = 0xff8a45;
      dealDamage(world.hitCtx, enemy, 135, x, y, false);
    });
  }

  private firePrismBurst(world: World): void {
    const p = world.player;
    for (let i = 0; i < 20; i++) {
      const projectile = world.projectiles.acquire();
      if (projectile === null) break;
      const angle = (i / 20) * Math.PI * 2;
      projectile.x = p.x;
      projectile.y = p.y;
      projectile.prevX = p.x;
      projectile.prevY = p.y;
      projectile.vx = Math.cos(angle) * 410;
      projectile.vy = Math.sin(angle) * 410;
      projectile.radius = 5;
      projectile.damage = 76;
      projectile.lifeSec = 1.7;
      projectile.color = 0x8cf6ff;
      projectile.pierceLeft = 1;
      projectile.id = this.nextAbilityProjectileId--;
      projectile.slowMul = 1;
      projectile.slowSec = 0;
      projectile.burnDps = 0;
      projectile.burnSec = 0;
      projectile.burstDamage = 0;
      projectile.burstRadius = 0;
      projectile.terrainBounces = 0;
      projectile.splitCount = 2;
      projectile.splitDamage = 38;
      projectile.bounceChainCount = 0;
      projectile.bounceChainDamage = 0;
      projectile.bounceChainRange = 0;
      projectile.railChargedSec = 0;
      projectile.sprite = 'prismBolt';
    }
  }

  /** Shared init for jutsu projectiles (pool objects must be fully reset). */
  private spawnAbilityProjectile(
    world: World,
    x: number,
    y: number,
    vx: number,
    vy: number,
    radius: number,
    damage: number,
    lifeSec: number,
    color: number,
    pierce: number,
    sprite: string,
    burnDps = 0,
    burnSec = 0,
  ): void {
    const projectile = world.projectiles.acquire();
    if (projectile === null) return;
    projectile.x = x;
    projectile.y = y;
    projectile.prevX = x;
    projectile.prevY = y;
    projectile.vx = vx;
    projectile.vy = vy;
    projectile.radius = radius;
    projectile.damage = damage;
    projectile.lifeSec = lifeSec;
    projectile.color = color;
    projectile.pierceLeft = pierce;
    projectile.id = this.nextAbilityProjectileId--;
    projectile.slowMul = 1;
    projectile.slowSec = 0;
    projectile.burnDps = burnDps;
    projectile.burnSec = burnSec;
    projectile.burstDamage = 0;
    projectile.burstRadius = 0;
    projectile.terrainBounces = 0;
    projectile.splitCount = 0;
    projectile.splitDamage = 0;
    projectile.bounceChainCount = 0;
    projectile.bounceChainDamage = 0;
    projectile.bounceChainRange = 0;
    projectile.railChargedSec = 0;
    projectile.sprite = sprite;
  }

  /** Ninja Disc: a ring of razor discs. Outward from the ninja, or - for the return
   * wave - spawned on the ring's edge flying back through everything in between. */
  private fireDiscRing(world: World, inward: boolean): void {
    const p = world.player;
    const count = 14;
    const speed = 430;
    const ringRadius = 330;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + (inward ? 0.22 : 0);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const x = inward ? p.x + cos * ringRadius : p.x;
      const y = inward ? p.y + sin * ringRadius : p.y;
      const direction = inward ? -1 : 1;
      this.spawnAbilityProjectile(
        world, x, y, cos * speed * direction, sin * speed * direction,
        6, 70, inward ? 0.9 : 0.85, 0x59ffd0, 3, 'windShuriken',
      );
    }
  }

  /** Fire Jutsu: a cone of flame bolts breathed in the ninja's facing direction. */
  private fireConeWave(world: World): void {
    const p = world.player;
    const heading = Math.atan2(world.movementY, world.movementX);
    const count = 9;
    const halfAngle = 0.5;
    for (let i = 0; i < count; i++) {
      const angle = heading - halfAngle + (i / (count - 1)) * halfAngle * 2;
      const speed = world.randomRange(300, 360);
      this.spawnAbilityProjectile(
        world, p.x, p.y, Math.cos(angle) * speed, Math.sin(angle) * speed,
        6, 42, 0.55, 0xff7a2a, 1, 'statusFlame', 18, 3,
      );
    }
  }

  /** Blue Lightning: while charged, keep zapping the nearest foe with a blue arc. */
  private blueLightningZap(world: World): void {
    const enemy = this.nearestEnemy(world, world.player.x, world.player.y, 480);
    if (enemy === null) return;
    spawnBeam(world.beams, world.player.x, world.player.y, enemy.x, enemy.y, 0x59a8ff, 'lightning');
    dealDamage(world.hitCtx, enemy, 60, enemy.x, enemy.y, false);
  }

  /** Spirit Smash: twin spirit lasers sweeping opposite sides of the ninja. */
  private performSpiritSweep(world: World): void {
    const p = world.player;
    const angle = this.spiritSweepIndex++ * 0.24;
    for (let beam = 0; beam < 2; beam++) {
      const sweep = angle + beam * Math.PI;
      const x2 = p.x + Math.cos(sweep) * 520;
      const y2 = p.y + Math.sin(sweep) * 520;
      spawnBeam(world.beams, p.x, p.y, x2, y2, 0xc8c8f0, 'solar');
      for (let i = 0; i < world.enemies.count; i++) {
        const enemy = world.enemies.items[i];
        if (enemy.hp <= 0) continue;
        if (this.distanceToSegment(enemy.x, enemy.y, p.x, p.y, x2, y2) > 24 + enemy.radius) continue;
        enemy.slowSec = Math.max(enemy.slowSec, 1.2);
        enemy.slowMul = Math.min(enemy.slowMul, 0.5);
        enemy.slowTint = 0xc8c8f0;
        dealDamage(world.hitCtx, enemy, 30, enemy.x, enemy.y, false);
      }
    }
  }

  private orbitGuardVolley(world: World): void {
    const visited = new Set<Enemy>();
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + world.elapsedSec * 2;
      const x = world.player.x + Math.cos(angle) * 46;
      const y = world.player.y + Math.sin(angle) * 46;
      const enemy = this.nearestEnemy(world, x, y, 520, (candidate) => !visited.has(candidate));
      if (enemy === null) continue;
      visited.add(enemy);
      spawnBeam(world.beams, x, y, enemy.x, enemy.y, 0xffc266, 'straight');
      dealDamage(world.hitCtx, enemy, 42, enemy.x, enemy.y, false);
    }
  }

  private interceptHostileShots(world: World): void {
    const radius2 = 86 * 86;
    for (let i = world.enemyShots.count - 1; i >= 0; i--) {
      const shot = world.enemyShots.items[i];
      if (this.distanceSquared(shot.x, shot.y, world.player.x, world.player.y) > radius2) continue;
      world.fxBurst(shot.x, shot.y, 0xffc266, 5);
      world.enemyShots.releaseAt(i);
    }
  }

  private performSolarSweep(world: World): void {
    const angle = this.solarPulseIndex++ * 0.22;
    const x2 = world.player.x + Math.cos(angle) * 540;
    const y2 = world.player.y + Math.sin(angle) * 540;
    spawnBeam(world.beams, world.player.x, world.player.y, x2, y2, 0xff8a45, 'solar');
    for (let i = 0; i < world.enemies.count; i++) {
      const enemy = world.enemies.items[i];
      if (enemy.hp <= 0) continue;
      if (this.distanceToSegment(enemy.x, enemy.y, world.player.x, world.player.y, x2, y2) > 24 + enemy.radius) continue;
      enemy.burnSec = Math.max(enemy.burnSec, 0.7);
      enemy.burnDps = Math.max(enemy.burnDps, 26);
      enemy.burnTint = 0xff8a45;
      dealDamage(world.hitCtx, enemy, 24, enemy.x, enemy.y, false);
    }
  }

  private fireConduitPylon(world: World, pylon: ArenaAbilityAgent): void {
    let x = pylon.x;
    let y = pylon.y;
    const visited = new Set<Enemy>();
    for (let i = 0; i < 4; i++) {
      const enemy = this.nearestEnemy(world, x, y, 340, (candidate) => !visited.has(candidate));
      if (enemy === null) break;
      visited.add(enemy);
      spawnBeam(world.beams, x, y, enemy.x, enemy.y, 0xf260ce, 'lightning');
      dealDamage(world.hitCtx, enemy, 58, enemy.x, enemy.y, false);
      x = enemy.x;
      y = enemy.y;
    }
  }

  private damageAlongRail(world: World, rail: ArenaAbilityField): void {
    const x2 = rail.x2 ?? rail.x;
    const y2 = rail.y2 ?? rail.y;
    for (let i = 0; i < world.enemies.count; i++) {
      const enemy = world.enemies.items[i];
      if (enemy.hp <= 0) continue;
      if (this.distanceToSegment(enemy.x, enemy.y, rail.x, rail.y, x2, y2) > rail.radius + enemy.radius) continue;
      enemy.slowSec = Math.max(enemy.slowSec, 0.32);
      enemy.slowMul = Math.min(enemy.slowMul, 0.55);
      enemy.slowTint = 0x74edff;
      dealDamage(world.hitCtx, enemy, 36, enemy.x, enemy.y, false);
    }
  }

  private updateArenaVisuals(dt: number, world: World): void {
    for (let i = this.arenaVisuals.length - 1; i >= 0; i--) {
      const visual = this.arenaVisuals[i];
      visual.lifeSec -= dt;
      if (visual.lifeSec > 0) continue;
      if (visual.kind === 'meteor') this.impactMeteor(world, visual.x, visual.y);
      this.arenaVisuals.splice(i, 1);
    }
  }

  private spawnLocalizedBurst(
    x: number,
    y: number,
    texture: string,
    color: number,
    radius: number,
    count: number,
    size: number,
    alternateColor?: number,
  ): void {
    this.arenaVisuals.push({
      kind: 'localizedBurst',
      texture,
      x,
      y,
      radius,
      color,
      alternateColor,
      count,
      size,
      rotation: 0,
      lifeSec: 0.34,
      maxLifeSec: 0.34,
    });
  }

  private pullEnemiesToward(
    world: World,
    x: number,
    y: number,
    radius: number,
    step: number,
  ): void {
    const radius2 = radius * radius;
    for (let i = 0; i < world.enemies.count; i++) {
      const enemy = world.enemies.items[i];
      if (enemy.hp <= 0 || enemy.isBoss) continue;
      this.pullPosition(enemy, x, y, radius2, step);
    }
  }

  private pullEnemyShotsToward(
    world: World,
    x: number,
    y: number,
    radius: number,
    step: number,
  ): void {
    const radius2 = radius * radius;
    for (let i = 0; i < world.enemyShots.count; i++) {
      this.pullPosition(world.enemyShots.items[i], x, y, radius2, step);
    }
  }

  private pullExperience(world: World, radius: number, speed: number, dt: number): void {
    const radius2 = radius * radius;
    for (let i = 0; i < world.motes.count; i++) {
      this.pullPosition(world.motes.items[i], world.player.x, world.player.y, radius2, speed * dt);
    }
  }

  private pullPosition(
    target: { x: number; y: number },
    x: number,
    y: number,
    radius2: number,
    step: number,
  ): void {
    const dx = x - target.x;
    const dy = y - target.y;
    const distance2 = dx * dx + dy * dy;
    if (distance2 <= 1 || distance2 > radius2) return;
    const distance = Math.sqrt(distance2);
    const movement = Math.min(step, distance) / distance;
    target.x += dx * movement;
    target.y += dy * movement;
  }

  private moveToward(
    target: { x: number; y: number },
    x: number,
    y: number,
    speed: number,
    dt: number,
  ): void {
    this.pullPosition(target, x, y, Number.POSITIVE_INFINITY, speed * dt);
  }

  private nearestEnemy(
    world: World,
    x: number,
    y: number,
    radius: number,
    predicate: (enemy: Enemy) => boolean = () => true,
  ): Enemy | null {
    let nearest: Enemy | null = null;
    let best = radius * radius;
    for (let i = 0; i < world.enemies.count; i++) {
      const enemy = world.enemies.items[i];
      if (enemy.hp <= 0 || !predicate(enemy)) continue;
      const distance2 = this.distanceSquared(x, y, enemy.x, enemy.y);
      if (distance2 > best) continue;
      best = distance2;
      nearest = enemy;
    }
    return nearest;
  }

  private forEachEnemyInRadius(
    world: World,
    x: number,
    y: number,
    radius: number,
    action: (enemy: Enemy) => void,
  ): void {
    const radius2 = radius * radius;
    for (let i = 0; i < world.enemies.count; i++) {
      const enemy = world.enemies.items[i];
      if (enemy.hp <= 0 || this.distanceSquared(x, y, enemy.x, enemy.y) > radius2) continue;
      action(enemy);
    }
  }

  private damageAreaAt(
    world: World,
    x: number,
    y: number,
    radius: number,
    damage: number,
  ): void {
    this.forEachEnemyInRadius(world, x, y, radius, (enemy) => {
      dealDamage(world.hitCtx, enemy, damage, x, y, false);
    });
  }

  private distanceSquared(x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return dx * dx + dy * dy;
  }

  private distanceToSegment(
    x: number,
    y: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): number {
    const vx = x2 - x1;
    const vy = y2 - y1;
    const length2 = Math.max(1, vx * vx + vy * vy);
    const projection = Math.max(0, Math.min(1, ((x - x1) * vx + (y - y1) * vy) / length2));
    return Math.hypot(x - (x1 + vx * projection), y - (y1 + vy * projection));
  }

  // --- Area helpers (operate on enemies near the player) ---

  private damageArea(world: World, radius: number, dmg: number): void {
    const p = world.player;
    const n = world.hash.query(p.x, p.y, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      if (dx * dx + dy * dy <= r2) dealDamage(world.hitCtx, e, dmg, e.x, e.y, false);
    }
  }

  private freezeArea(world: World, radius: number, slowSec: number, slowMul: number): void {
    const p = world.player;
    const n = world.hash.query(p.x, p.y, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      if (dx * dx + dy * dy <= r2) {
        e.slowSec = Math.max(e.slowSec, slowSec);
        e.slowMul = Math.min(e.slowMul, slowMul);
      }
    }
  }

  private igniteArea(world: World, radius: number, burnSec: number, burnDps: number): void {
    const p = world.player;
    const n = world.hash.query(p.x, p.y, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      if (dx * dx + dy * dy <= r2) {
        e.burnSec = Math.max(e.burnSec, burnSec);
        e.burnDps = Math.max(e.burnDps, burnDps);
      }
    }
  }

  private knockback(world: World, radius: number, force: number): void {
    const p = world.player;
    const n = world.hash.query(p.x, p.y, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= r2 && d2 > 0.001) {
        const d = Math.sqrt(d2);
        e.x += (dx / d) * force;
        e.y += (dy / d) * force;
      }
    }
  }

  private pull(world: World, radius: number, step: number): void {
    const p = world.player;
    const n = world.hash.query(p.x, p.y, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0 || e.isBoss) continue; // don't yank bosses around
      const dx = p.x - e.x;
      const dy = p.y - e.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= r2 && d2 > 1) {
        const d = Math.sqrt(d2);
        const s = Math.min(step, d);
        e.x += (dx / d) * s;
        e.y += (dy / d) * s;
      }
    }
  }

  private overloadZap(world: World, color: number): void {
    const p = world.player;
    const n = world.hash.query(p.x, p.y, ABILITY.overloadRadius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = ABILITY.overloadRadius * ABILITY.overloadRadius;
    let hits = 0;
    for (let j = 0; j < n && hits < ABILITY.overloadTargets; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      if (dx * dx + dy * dy > r2) continue;
      spawnBeam(world.beams, p.x, p.y, e.x, e.y, color);
      dealDamage(world.hitCtx, e, ABILITY.overloadDamage, e.x, e.y, false);
      hits++;
    }
  }
}
